import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM, Text, Button, Icon, Spinner } from 'components/atoms';
import Breadcrumb from 'components/molecules/Breadcrumb';
import { useRunningExperiments, useExperiments, useExperiment, useExperimentProgress } from 'hooks/aiReviewTest/useExperiments';
import { TestExperiment as Experiment, TestStatus } from 'types/aiReviewTest';
import RunningExperiments from './RunningExperiments';
import ExperimentResultsTable from './ExperimentResultsTable';
import ExperimentDetailModal from './ExperimentDetailModal';
import { SlideManager } from 'components/organisms/CommonUI';
import { connectEvaluationStream, disconnectEvaluationStream, TestProgress } from 'api/aiReviewEvaluationService';
import { useQueryClient } from 'react-query';
import { experimentApi } from 'services/api/aiReviewTest/experimentApi';
import { ToastManager } from 'components/organisms/CommonUI';


const AdminAIReviewTestResultsPage: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<TestStatus | 'all'>('all');
  const queryClient = useQueryClient();

  const { data: runningExperiments, isLoading: isLoadingRunning, refetch: refetchRunning } = useRunningExperiments();
  const { data: experiments, isLoading: isLoadingExperiments, refetch: refetchExperiments } = useExperiments(
    selectedStatus === 'all' ? undefined : selectedStatus
  );

  // 실행 중인 실험들에 대한 스트림 연결 관리
  useEffect(() => {
    const runningExperimentList = runningExperiments?.data?.data || [];
    const failedCountMap = new Map<number, number>();
    
    // 실행 중인 실험들에 대해 스트림 연결
    runningExperimentList.forEach((experiment) => {
      if (experiment.status === TestStatus.RUNNING) {
        connectEvaluationStream(
          experiment.id,
          // onProgress
          (progress: TestProgress) => {
            // 실패 카운트 누적
            if (progress.status === 'failed') {
              const currentFailed = failedCountMap.get(experiment.id) || 0;
              failedCountMap.set(experiment.id, currentFailed + 1);
            }
            
            // React Query 캐시 업데이트 - TestProgress 형식에 맞게 변환
            queryClient.setQueryData(['experiments', experiment.id, 'progress'], {
              data: { 
                data: {
                  total: progress.totalItems,
                  completed: progress.currentItemIndex,
                  failed: failedCountMap.get(experiment.id) || 0,
                  currentTask: progress.currentItem,
                  errorCount: failedCountMap.get(experiment.id) || 0,
                  message: progress.message
                }
              }
            });
          },
          // onStatus
          (status) => {
            // 상태가 변경되면 실험 목록 다시 불러오기
            if (status !== TestStatus.RUNNING) {
              refetchRunning();
              refetchExperiments();
            }
          },
          // onComplete
          () => {
            // 완료되면 실험 목록 다시 불러오기
            refetchRunning();
            refetchExperiments();
          },
          // onError
          (error) => {
            console.error('Stream error for experiment', experiment.id, error);
          }
        );
      }
    });

    // 클린업: 컴포넌트 언마운트 시 스트림 연결 해제
    return () => {
      runningExperimentList.forEach((experiment) => {
        if (experiment.status === TestStatus.RUNNING) {
          disconnectEvaluationStream(experiment.id);
        }
      });
    };
  }, [runningExperiments, queryClient, refetchRunning, refetchExperiments]);


  const statusOptions = [
    { value: 'all', label: '전체' },
    { value: TestStatus.PENDING, label: '대기중' },
    { value: TestStatus.RUNNING, label: '실행중' },
    { value: TestStatus.COMPLETED, label: '완료' },
    { value: TestStatus.FAILED, label: '실패' },
  ];

  const handleExperimentClick = (experiment: Experiment) => {
    const handleStopExperiment = async () => {
      try {
        await experimentApi.stop(experiment.id);
        ToastManager.show({
          content: '실험이 정지되었습니다.',
          duration: 2000,
        });
        // 모달 닫기
        SlideManager.hide();
        // 데이터 새로고침
        refetchRunning();
        refetchExperiments();
      } catch (error) {
        console.error('실험 정지 실패:', error);
        ToastManager.show({
          content: '실험 정지에 실패했습니다.',
          duration: 3000,
        });
      }
    };

    const handleResumeExperiment = async () => {
      try {
        await experimentApi.start(experiment.id, false);
        ToastManager.show({
          content: '실험이 재개되었습니다.',
          duration: 2000,
        });
        // 모달 닫기
        SlideManager.hide();
        // 데이터 새로고침
        refetchRunning();
        refetchExperiments();
      } catch (error) {
        console.error('실험 재개 실패:', error);
        ToastManager.show({
          content: '실험 재개에 실패했습니다.',
          duration: 3000,
        });
      }
    };

    const handleRestartExperiment = async () => {
      try {
        await experimentApi.start(experiment.id, true);
        ToastManager.show({
          content: '실험이 처음부터 재시작되었습니다.',
          duration: 2000,
        });
        SlideManager.hide();
        refetchRunning();
        refetchExperiments();
      } catch (error) {
        console.error('실험 재시작 실패:', error);
        ToastManager.show({
          content: '실험 재시작에 실패했습니다.',
          duration: 3000,
        });
      }
    };

    const handleDuplicateExperiment = async (iterations: number) => {
      try {
        const duplicateConfig = experiment.configuration.map((config: any) => ({
          aiReviewRequest: {
            modelType: config.aiReviewRequest.modelType,
            priority: config.aiReviewRequest.priority,
            promptType: config.aiReviewRequest.promptType,
            promptId: config.aiReviewRequest.promptId,
            medicationOptions: config.aiReviewRequest.medicationOptions,
            docsOptions: config.aiReviewRequest.docsOptions,
            evaluationPromptId: config.aiReviewRequest.evaluationPromptId,
            evaluationModel: config.aiReviewRequest.evaluationModel,
            evaluationUseGTSnapshot: config.aiReviewRequest.evaluationUseGTSnapshot,
          },
          patientIds: config.patientIds,
          aiReviewIds: config.aiReviewIds,
          aiReviewEvaluationIds: config.aiReviewEvaluationIds,
          iterations,
        }));

        const response = await experimentApi.create({
          name: `${experiment.name || '실험'} (추가 ${iterations}회)`,
          description: experiment.description,
          configuration: duplicateConfig,
        });

        if (response.data.data?.id) {
          await experimentApi.start(response.data.data.id);
          ToastManager.show({
            content: `같은 설정으로 ${iterations}회 추가 실행을 시작했습니다.`,
            duration: 2000,
          });
        }

        SlideManager.hide();
        queryClient.invalidateQueries(['experiments']);
        refetchRunning();
        refetchExperiments();
      } catch (error) {
        console.error('실험 추가 실행 실패:', error);
        ToastManager.show({
          content: '실험 추가 실행에 실패했습니다.',
          duration: 3000,
        });
      }
    };

    SlideManager.show({
      body: (
        <ExperimentDetailModal
          experimentId={experiment.id}
          onClose={() => {
            SlideManager.hide();
          }}
          onStop={handleStopExperiment}
          onResume={handleResumeExperiment}
          onRestart={handleRestartExperiment}
          onDuplicate={handleDuplicateExperiment}
        />
      ),
      width: '1200px',
      needCloseButton: true,
      direction: 'right',
    });
  };

  const handleRefresh = () => {
    refetchRunning();
    refetchExperiments();
  };

  return (
    <Container>
      <Header>
        <Breadcrumb />
        <TitleSection>
          <Title>테스트 결과</Title>
          <Description>AI Review 자동화 테스트 실행 결과를 확인하고 관리합니다.</Description>
        </TitleSection>
      </Header>

      {/* 실행 중인 실험 섹션 */}
      {isLoadingRunning ? (
        <LoadingContainer>
          <Spinner size="Medium" />
        </LoadingContainer>
      ) : runningExperiments?.data?.data && runningExperiments.data.data.length > 0 ? (
        <RunningExperiments 
          experiments={runningExperiments.data.data}
          onExperimentClick={handleExperimentClick}
        />
      ) : (
        <NoRunningExperiments>
          <Icon name="Activity" size={24} color={COLOR_SYSTEM.get("Gray")?.[400]} />
          <NoRunningText>현재 실행 중인 실험이 없습니다.</NoRunningText>
        </NoRunningExperiments>
      )}

      {/* 실험 결과 테이블 섹션 */}
      <TableSection>
        <TableHeader>
          <TableTitle>실험 목록</TableTitle>
          <FilterGroup>
            <Button
              size="Small"
              type="IconWithText"
              variant="Outlined"
              color="Secondary"
              icon="Refresh"
              left
              onClick={handleRefresh}
              disabled={isLoadingRunning || isLoadingExperiments}
            >
              새로고침
            </Button>
            <StatusSelect
              value={selectedStatus}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedStatus(e.target.value as TestStatus | 'all')}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StatusSelect>
          </FilterGroup>
        </TableHeader>

        {isLoadingExperiments ? (
          <LoadingContainer>
            <Spinner size="Large" />
          </LoadingContainer>
        ) : (
          <ExperimentResultsTable
            experiments={experiments?.data?.data || []}
            onExperimentClick={handleExperimentClick}
          />
        )}
      </TableSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled(Text)`
  font-size: 24px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const Description = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  
  button {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const StatusSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  cursor: pointer;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: ${COLOR_SYSTEM.get("Blue")?.[500]};
  }
`;

const NoRunningExperiments = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border: 2px dashed ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 12px;
`;

const NoRunningText = styled(Text)`
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

export default AdminAIReviewTestResultsPage;